import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Video, X, ShieldCheck, Zap, BadgeCheck, Loader2, Copy, Star, Users, Crown } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { bookVideoConsultation } from "../lib/payments";

export default function VideoConsultModal({ open, onClose, onBooked }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [engineers, setEngineers] = useState([]);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    description: "",
    preferred_staff_id: "", // empty = "Any available"
  });
  const [step, setStep] = useState("form"); // form | paying | done
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    api.get("/payments/video/config").then(({ data }) => setConfig(data)).catch(() => {});
    api.get("/public/engineers").then(({ data }) => setEngineers(Array.isArray(data) ? data : [])).catch(() => setEngineers([]));
  }, [open]);

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, name: f.name || user.name, email: f.email || user.email, phone: f.phone || user.phone || "" }));
  }, [user, open]);

  if (!open) return null;

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const selectedEngineer = engineers.find((e) => e.id === form.preferred_staff_id) || null;
  const selectedPrice = selectedEngineer ? selectedEngineer.price_inr : (config?.base_inr ?? config?.amount_inr ?? 200);
  const isPremium = !!selectedEngineer?.is_premium;

  const pay = async (e) => {
    e.preventDefault();
    setStep("paying");
    try {
      const data = await bookVideoConsultation(form);
      setResult(data);
      setStep("done");
      toast.success(data.mock ? "Payment simulated — ticket created!" : "Payment successful — ticket created!");
      onBooked?.(data.ticket);
    } catch (err) {
      setStep("form");
      toast.error(formatApiError(err) || "Payment failed");
    }
  };

  const closeAndReset = () => {
    setStep("form");
    setResult(null);
    setForm({ name: user?.name || "", email: user?.email || "", phone: user?.phone || "", subject: "", description: "", preferred_staff_id: "" });
    onClose?.();
  };

  const goToDashboard = () => {
    closeAndReset();
    if (user) navigate("/dashboard");
    else navigate("/login");
  };

  return (
    <div className="fixed inset-0 z-[60] grid place-items-center bg-black/70 p-4 overflow-y-auto" data-testid="video-consult-modal">
      <div className="w-full max-w-xl rounded-2xl border border-blue-500/20 bg-[#0F172A] shadow-2xl overflow-hidden my-8">
        {/* Header */}
        <div className="relative bg-gradient-to-br from-blue-600/20 via-[#111827] to-[#0F172A] p-6 border-b border-white/10">
          <button onClick={closeAndReset} className="absolute top-4 right-4 text-white/50 hover:text-white" data-testid="video-modal-close"><X className="h-5 w-5" /></button>
          <div className="flex items-center gap-3">
            <div className="grid h-12 w-12 place-items-center rounded-xl bg-gradient-to-br from-blue-500 to-cyan-400 text-[#0A101D]">
              <Video className="h-6 w-6" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-blue-400 font-semibold">Instant Expert Support</div>
              <h3 className="text-xl font-bold">Live Video Call with an Engineer</h3>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-baseline gap-2">
            <span className="text-4xl font-extrabold stat-num transition-all" data-testid="video-price-display">
              ₹{selectedPrice}
            </span>
            {isPremium ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 border border-amber-500/40 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider text-amber-300" data-testid="video-premium-badge">
                <Crown className="h-3 w-3" /> Premium · top-rated engineer
              </span>
            ) : (
              <span className="text-sm text-white/60">one-time · INR · GST extra if applicable</span>
            )}
          </div>
          <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 px-2 py-0.5 text-emerald-300"><Zap className="h-3 w-3" /> Connect in &lt; 5 min</span>
            <span className="inline-flex items-center gap-1 rounded-full bg-blue-500/10 border border-blue-500/30 px-2 py-0.5 text-blue-300"><BadgeCheck className="h-3 w-3" /> Money-back if not resolved</span>
            <span className="inline-flex items-center gap-1 rounded-full bg-white/5 border border-white/10 px-2 py-0.5 text-white/70"><ShieldCheck className="h-3 w-3" /> 256-bit secure</span>
          </div>
          {config?.mock_mode && (
            <div className="mt-4 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200" data-testid="video-mock-badge">
              <strong>Mock payment mode active.</strong> Live Razorpay keys haven&apos;t been added yet — the ₹200 charge will be simulated. Switch to live mode by setting RAZORPAY_KEY_ID/SECRET in backend env.
            </div>
          )}
        </div>

        {/* Body */}
        {step === "form" && (
          <form onSubmit={pay} className="p-6 space-y-4" data-testid="video-consult-form">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs uppercase tracking-wider text-white/40 mb-1.5">Full name</label>
                <input className="input-field" name="name" required value={form.name} onChange={onChange} data-testid="video-name" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-white/40 mb-1.5">Email</label>
                <input className="input-field" type="email" name="email" required value={form.email} onChange={onChange} data-testid="video-email" />
              </div>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-white/40 mb-1.5">Phone</label>
              <input className="input-field" name="phone" value={form.phone} onChange={onChange} placeholder="So we can call back if disconnected" data-testid="video-phone" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-white/40 mb-1.5">Issue summary</label>
              <input className="input-field" name="subject" required value={form.subject} onChange={onChange} placeholder='e.g. "Printer keeps showing offline"' data-testid="video-subject" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-white/40 mb-1.5">Describe the problem</label>
              <textarea rows={3} className="input-field !h-auto" name="description" required value={form.description} onChange={onChange} data-testid="video-description" />
            </div>

            {/* Engineer picker */}
            <div>
              <label className="block text-xs uppercase tracking-wider text-white/40 mb-2 flex items-center gap-1.5">
                <Users className="h-3 w-3" /> Pick your engineer <span className="text-white/30">(optional)</span>
              </label>
              <div className="grid sm:grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, preferred_staff_id: "" }))}
                  className={`text-left rounded-lg border px-3 py-2.5 transition-all ${
                    form.preferred_staff_id === "" ? "border-blue-500 bg-blue-500/10" : "border-white/10 bg-white/[0.02] hover:border-white/25"
                  }`}
                  data-testid="engineer-pick-any"
                >
                  <div className="flex items-center gap-2.5">
                    <div className="grid h-9 w-9 place-items-center rounded-full bg-blue-500/15 border border-blue-500/30 text-blue-300">
                      <Zap className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold">Any available</div>
                      <div className="text-[11px] text-white/45">Fastest connection</div>
                    </div>
                    <span className="text-[11px] font-bold text-blue-300 whitespace-nowrap">₹{config?.base_inr ?? 200}</span>
                  </div>
                </button>
                {engineers.map((e) => {
                  const active = form.preferred_staff_id === e.id;
                  const initials = (e.name || "").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
                  const ringCls = active
                    ? (e.is_premium ? "border-amber-500 bg-amber-500/10" : "border-emerald-500 bg-emerald-500/10")
                    : "border-white/10 bg-white/[0.02] hover:border-white/25";
                  return (
                    <button
                      type="button"
                      key={e.id}
                      onClick={() => setForm((f) => ({ ...f, preferred_staff_id: e.id }))}
                      className={`text-left rounded-lg border px-3 py-2.5 transition-all relative ${ringCls}`}
                      data-testid={`engineer-pick-${e.id}`}
                    >
                      {e.is_premium && (
                        <span className="absolute -top-2 -right-2 inline-flex items-center gap-0.5 rounded-full bg-amber-500/90 border border-amber-300 px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-wider text-[#0A101D] shadow-md" data-testid={`engineer-premium-${e.id}`}>
                          <Crown className="h-2.5 w-2.5" /> Premium
                        </span>
                      )}
                      <div className="flex items-center gap-2.5">
                        <div className="grid h-9 w-9 place-items-center rounded-full bg-gradient-to-br from-blue-500/30 to-cyan-400/30 border border-white/10 text-xs font-bold">
                          {initials || "EN"}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold truncate flex items-center gap-1.5">
                            {e.name}
                            {e.avg_rating > 0 && (
                              <span className="flex items-center gap-0.5 text-[10px] text-amber-300 font-semibold whitespace-nowrap">
                                <Star className="h-2.5 w-2.5 fill-amber-300" /> {e.avg_rating.toFixed(2)}
                              </span>
                            )}
                          </div>
                          <div className="text-[11px] text-white/45 truncate">{e.specialization}</div>
                        </div>
                        <span className={`text-[11px] font-bold whitespace-nowrap ${e.is_premium ? "text-amber-300" : "text-blue-300"}`} data-testid={`engineer-price-${e.id}`}>
                          ₹{e.price_inr}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
              {isPremium && (
                <p className="mt-2 text-[11px] text-amber-200/80 flex items-start gap-1.5" data-testid="premium-upsell-note">
                  <Crown className="h-3 w-3 mt-0.5" />
                  You&apos;re booking a top-rated engineer directly. Premium pricing applies — same money-back guarantee.
                </p>
              )}
            </div>

            <button type="submit" className={`btn-primary w-full text-base !py-3.5 ${isPremium ? "!bg-gradient-to-r !from-amber-500 !to-amber-600 hover:!from-amber-600 hover:!to-amber-700" : ""}`} data-testid="video-pay-btn">
              <Video className="h-4 w-4" /> Pay ₹{selectedPrice} & Start Video Call
            </button>
            <p className="text-[11px] text-white/40 text-center">By proceeding you agree to our terms. We&apos;ll create a high-priority ticket and a private Jitsi meeting room.</p>
          </form>
        )}

        {step === "paying" && (
          <div className="p-10 flex flex-col items-center gap-4" data-testid="video-paying">
            <Loader2 className="h-10 w-10 animate-spin text-blue-400" />
            <div className="text-lg font-semibold">Processing payment…</div>
            <div className="text-sm text-white/60 text-center max-w-sm">
              {config?.mock_mode ? "Simulating Razorpay checkout (mock mode)." : "Confirming with Razorpay. Do not refresh the page."}
            </div>
          </div>
        )}

        {step === "done" && result?.ticket && (
          <div className="p-6 space-y-4" data-testid="video-done">
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4">
              <div className="text-xs uppercase tracking-widest text-emerald-300 font-semibold">Payment successful</div>
              <div className="mt-1 text-sm text-white/80">
                Ticket <span className="font-mono text-emerald-300">{result.ticket.ticket_number}</span> created with <strong>CRITICAL</strong> priority. An engineer will join you shortly.
              </div>
              {result.ticket.assigned_to_name && (
                <div className="mt-2 text-xs text-emerald-200/80" data-testid="video-assigned-engineer">
                  Assigned to <strong>{result.ticket.assigned_to_name}</strong> — they&apos;ve been notified.
                </div>
              )}
            </div>
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
              <div className="text-xs uppercase tracking-widest text-white/40 font-semibold">Your private meeting room</div>
              <div className="mt-2 flex items-center gap-2">
                <input readOnly value={result.ticket.meeting_url} className="input-field !text-xs" data-testid="video-meeting-link" />
                <button
                  className="btn-secondary !py-2 !px-3 text-xs"
                  onClick={() => { navigator.clipboard?.writeText(result.ticket.meeting_url); toast.success("Link copied"); }}
                  data-testid="video-copy-link"
                >
                  <Copy className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <a
                href={result.ticket.meeting_url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary flex-1"
                data-testid="video-join-now"
              >
                <Video className="h-4 w-4" /> Join Video Call Now
              </a>
              <button onClick={goToDashboard} className="btn-secondary flex-1" data-testid="video-go-dashboard">
                {user ? "Open Dashboard" : "Sign in to track"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
